import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut,
  Loader2,
  RefreshCw,
  Inbox,
  Mail,
  Phone,
  MapPin,
  CalendarRange,
  PawPrint,
  Lock,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { IMAGES } from "@/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "cp_admin_token";

const inputCls =
  "bg-background border-border text-foreground placeholder:text-muted-foreground/70 focus-visible:ring-ring h-12 rounded-xl";

function LoginView({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem(TOKEN_KEY, data.access_token);
      toast.success("Welcome back!");
      onLogin(data.access_token);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid place-items-center px-5 bg-background">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-md rounded-3xl bg-card border border-black/5 shadow-[0_20px_60px_-25px_rgba(93,87,107,0.4)] p-8 sm:p-10"
      >
        <div className="flex flex-col items-center text-center mb-8">
          <img src={IMAGES.logoHorizontal} alt="Cheeky Pups" className="h-12 w-auto mb-4" />
          <div className="text-xs text-muted-foreground flex items-center gap-1 font-semibold">
            <Lock size={11} /> Admin area
          </div>
        </div>

        <div className="space-y-5">
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-primary/80">Username</Label>
            <Input data-testid="admin-username" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Username" className={inputCls} autoComplete="username" />
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-primary/80">Password</Label>
            <Input data-testid="admin-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password" className={inputCls} autoComplete="current-password" />
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-submit"
          className="mt-8 w-full inline-flex items-center justify-center gap-2 rounded-full bg-primary py-3.5 text-base font-bold text-primary-foreground hover:-translate-y-0.5 transition-all disabled:opacity-60"
        >
          {loading ? <Loader2 className="animate-spin" size={18} /> : <Lock size={16} />}
          {loading ? "Signing in…" : "Sign in"}
        </button>
        <a href="/" className="block text-center text-sm text-muted-foreground mt-6 hover:text-accent font-semibold">← Back to website</a>
      </form>
    </div>
  );
}

function petsText(pets) {
  if (!pets) return "—";
  const labels = [
    [pets.puppy, "puppy", "puppies"],
    [pets.small, "small dog", "small dogs"],
    [pets.medium, "medium dog", "medium dogs"],
    [pets.large, "large dog", "large dogs"],
  ];
  const parts = labels.filter(([n]) => n > 0).map(([n, s, p]) => `${n} ${n === 1 ? s : p}`);
  return parts.join(", ") || "—";
}

function scheduleText(b) {
  if (b.frequency === "weekly") {
    const days = (b.weekdays || []).join(", ") || "—";
    return `Weekly: ${days} (from ${b.start_week || "—"})`;
  }
  if (b.frequency === "one-off") {
    return "One-off: " + ((b.one_off_dates || []).join(", ") || "—");
  }
  return `${b.date_from || "—"} → ${b.date_until || "—"}`;
}

function BookingCard({ b, index }) {
  const created = new Date(b.created_at);
  return (
    <div
      data-testid={`admin-booking-${index}`}
      className="rounded-3xl bg-card border border-black/5 shadow-sm p-6"
    >
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <span className="inline-flex items-center gap-2 rounded-full bg-secondary/40 text-primary px-3 py-1 text-sm font-bold">
          <PawPrint size={14} className="text-accent" /> {b.service}
        </span>
        <span className="text-xs text-muted-foreground font-medium">
          {created.toLocaleDateString()} · {created.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </span>
      </div>

      <div className="font-display font-extrabold text-xl mb-1 text-primary">{b.owner_name}</div>
      <div className="text-sm text-muted-foreground mb-4 font-medium">{petsText(b.pets)}</div>

      <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm font-medium">
        <a href={`mailto:${b.email}`} className="flex items-center gap-2 text-primary/85 hover:text-accent"><Mail size={14} className="text-accent" /> {b.email}</a>
        <a href={`sms:${b.phone}`} className="flex items-center gap-2 text-primary/85 hover:text-accent"><Phone size={14} className="text-accent" /> {b.phone}</a>
        <div className="flex items-center gap-2 text-primary/85"><MapPin size={14} className="text-accent" /> {b.suburb}</div>
        <div className="flex items-center gap-2 text-primary/85"><CalendarRange size={14} className="text-accent shrink-0" /> {scheduleText(b)}</div>
      </div>

      {b.notes ? (
        <div className="mt-4 rounded-xl bg-muted p-3 text-sm text-muted-foreground font-medium">{b.notes}</div>
      ) : null}
      <div className="mt-4 text-xs text-muted-foreground font-medium">
        Owner email {b.email_sent ? "sent ✓" : "not sent"}
      </div>
    </div>
  );
}

function Dashboard({ token, onLogout }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/bookings`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBookings(data);
    } catch (err) {
      if (err.response?.status === 401) {
        onLogout();
      } else {
        toast.error("Could not load bookings");
      }
    } finally {
      setLoading(false);
    }
  }, [token, onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 bg-background/90 backdrop-blur-xl border-b border-border">
        <div className="mx-auto max-w-5xl px-5 sm:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={IMAGES.logoHorizontal} alt="Cheeky Pups" className="h-9 w-auto" />
            <div className="hidden sm:block border-l border-border pl-4">
              <div className="font-display font-extrabold text-lg leading-none text-primary">Bookings</div>
              <div className="text-xs text-muted-foreground font-medium">{bookings.length} total</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} data-testid="admin-refresh" className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-4 py-2 text-sm font-semibold text-primary hover:border-accent transition-colors">
              <RefreshCw size={15} /> Refresh
            </button>
            <button onClick={onLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-bold text-primary-foreground hover:-translate-y-0.5 transition-all">
              <LogOut size={15} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 sm:px-8 py-10">
        <h1 className="font-display font-extrabold text-3xl sm:text-5xl tracking-tight mb-8 text-primary">
          Booking requests
        </h1>

        {loading ? (
          <div className="flex items-center gap-3 text-muted-foreground py-20 justify-center font-medium">
            <Loader2 className="animate-spin" size={20} /> Loading…
          </div>
        ) : bookings.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground font-medium" data-testid="admin-empty">
            <Inbox size={40} className="mx-auto mb-4 text-accent" />
            No booking requests yet.
          </div>
        ) : (
          <div className="grid gap-5">
            {bookings.map((b, i) => (
              <BookingCard key={b.id} b={b} index={i} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

export default function Admin() {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY));

  const logout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken(null);
  };

  return token ? (
    <Dashboard token={token} onLogout={logout} />
  ) : (
    <LoginView onLogin={setToken} />
  );
}
